/**
 * LogicConditionForm - Клас для керування динамічними логічними умовами
 * Підтримує вкладені групи з AND/OR операторами
 */

class LogicConditionForm {
    constructor(formElement) {
        // Зберігаємо посилання на сам елемент форми
        this.form = formElement;
        
        if (!this.form) {
            console.error('Form element not provided');
            return;
        }
        
        this.groupsContainer = this.form.querySelector('.groups-container');
        this.hiddenInput = this.form.querySelector('input[name="logic_structure"]');
        this.groupCounter = 0;
        this.conditionCounter = 0;
        
        // Отримуємо переклади з глобального об'єкта
        const t = window.logicConditionTranslations || {};
        const ops = t.operators || {};
        
        this.operators = [
            { group: ops.basic?.group || 'Базові порівняння', options: [
                { value: '==', label: ops.basic?.equals || 'Дорівнює (==)' },
                { value: '!=', label: ops.basic?.not_equals || 'Не дорівнює (!=)' },
                { value: '>', label: ops.basic?.greater || 'Більше (>)' },
                { value: '<', label: ops.basic?.less || 'Менше (<)' },
                { value: '>=', label: ops.basic?.greater_or_equal || 'Більше або дорівнює (>=)' },
                { value: '<=', label: ops.basic?.less_or_equal || 'Менше або дорівнює (<=)' }
            ]},
            { group: ops.text?.group || 'Текстові перевірки', options: [
                { value: 'contains', label: ops.text?.contains || 'Містить' },
                { value: 'not_contains', label: ops.text?.not_contains || 'Не містить' },
                { value: 'starts_with', label: ops.text?.starts_with || 'Починається з' },
                { value: 'not_starts_with', label: ops.text?.not_starts_with || 'Не починається з' },
                { value: 'ends_with', label: ops.text?.ends_with || 'Закінчується на' },
                { value: 'not_ends_with', label: ops.text?.not_ends_with || 'Не закінчується на' }
            ]},
            { group: ops.empty?.group || 'Перевірки на порожність', options: [
                { value: 'is_empty', label: ops.empty?.is_empty || 'Порожнє' },
                { value: 'is_not_empty', label: ops.empty?.is_not_empty || 'Не порожнє' }
            ]},
            { group: ops.regex?.group || 'Regex', options: [
                { value: 'regex_match', label: ops.regex?.regex_match || 'Відповідає регулярному виразу' },
                { value: 'regex_not_match', label: ops.regex?.regex_not_match || 'Не відповідає регулярному виразу' }
            ]}
        ];
        
        this.init();
    }
    
    init() {
        // Додаємо першу групу
        this.addGroup();
        
        // Слухаємо зміни в формі
        this.form.addEventListener('input', (e) => {
            this.updateHiddenInput();
        });
        
        // Делегування подій для кнопок в групах
        this.groupsContainer.addEventListener('click', (e) => {
            const target = e.target.closest('button');
            if (!target) return;
            
            // Перевіряємо що кнопка належить цьому контейнеру
            if (target.closest('.groups-container') !== this.groupsContainer) return;
            
            this.handleClick(e);
        });
        
        // Делегування подій для кнопок додавання груп
        const addGroupButtons = this.form.querySelector('.add-group-buttons');
        
        if (addGroupButtons) {
            addGroupButtons.addEventListener('click', (e) => {
                const target = e.target.closest('button');
                if (!target || !target.dataset.operator) return;
                
                // Перевіряємо що кнопка належить цій формі
                if (target.closest('.logic-condition-dynamic-form') !== this.form) return;
                
                e.preventDefault();
                this.addGroup(target.dataset.operator);
            });
        }
        
        // Обробник для згортання/розгортання preview
        const previewHeader = this.form.closest('.logic-condition-dynamic-form')?.querySelector('.preview-header');
        if (previewHeader) {
            previewHeader.addEventListener('click', () => {
                const preview = this.form.closest('.logic-condition-dynamic-form')?.querySelector('.fixed-condition-preview');
                const content = preview?.querySelector('.preview-content');
                const icon = preview?.querySelector('.toggle-preview i');
                
                if (content && preview) {
                    content.classList.toggle('collapsed');
                    preview.classList.toggle('collapsed');
                    
                    if (icon) {
                        icon.classList.toggle('fa-chevron-down');
                        icon.classList.toggle('fa-chevron-up');
                    }
                }
            });
        }
    }
    
    handleClick(e) {
        const target = e.target.closest('button');
        if (!target) return;
        
        const action = target.dataset.action;
        if (!action) return;
        
        e.preventDefault();
        
        switch(action) {
            case 'add-condition':
                this.addCondition(target.dataset.groupId, target.dataset.operator);
                break;
            case 'remove-condition':
                this.removeCondition(target.dataset.conditionId, target.dataset.groupId);
                break;
            case 'remove-group':
                this.removeGroup(target.dataset.groupId);
                break;
        }
    }
    
    createOperatorSelect(selectedValue = '==') {
        let html = '<select class="form-control operator-select custom-small-input" required>';
        this.operators.forEach(group => {
            html += `<optgroup label="${group.group}">`;
            group.options.forEach(opt => {
                const selected = opt.value === selectedValue ? 'selected' : '';
                html += `<option value="${opt.value}" ${selected}>${opt.label}</option>`;
            });
            html += '</optgroup>';
        });
        html += '</select>';
        return html;
    }
    
    createCondition(groupId, operator = null, leftValue = '', operatorValue = '==', rightValue = '') {
        const t = window.logicConditionTranslations || {};
        const conditionId = `condition-${Date.now()}-${this.conditionCounter++}`;
        const operatorBadge = operator ? `<div class="condition-operator-badge ${operator.toLowerCase()}">${operator}</div>` : '';
        
        return `
            <div class="condition-row" data-condition-id="${conditionId}">
                ${operatorBadge}
                <div class="form-group">
                    <input type="text" class="form-control custom-small-input left-operand" 
                           placeholder="${t.placeholders?.left_value || 'Ліве значення'}" value="${leftValue}" required>
                </div>
                <div class="form-group operator-select">
                    ${this.createOperatorSelect(operatorValue)}
                </div>
                <div class="form-group right-operand-group">
                    <input type="text" class="form-control custom-small-input right-operand" 
                           placeholder="${t.placeholders?.right_value || 'Праве значення'}" value="${rightValue}">
                </div>
                <button type="button" class="btn btn-sm btn-danger remove-btn" 
                        data-action="remove-condition"
                        data-condition-id="${conditionId}"
                        data-group-id="${groupId}">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        `;
    }
    
    addGroup(operator = null) {
        const t = window.logicConditionTranslations || {};
        const groupId = `group-${Date.now()}-${this.groupCounter++}`;
        const operatorClass = operator ? operator.toLowerCase() + '-group' : '';
        const operatorBadge = operator ? `<span class="group-operator-badge ${operator.toLowerCase()}">${operator}</span>` : '';
        
        const html = `
            <div class="logic-group ${operatorClass}" data-group-id="${groupId}">
                <div class="group-header">
                    <div>
                        ${operatorBadge}
                        <span class="text-muted">${t.labels?.group_of_conditions || 'Група умов'}</span>
                    </div>
                    <button type="button" class="btn btn-sm btn-danger" 
                            data-action="remove-group"
                            data-group-id="${groupId}">
                        <i class="fas fa-trash"></i> ${t.buttons?.remove_group || 'Видалити групу'}
                    </button>
                </div>
                <div class="conditions-container" data-group-id="${groupId}">
                    ${this.createCondition(groupId)}
                </div>
                <div class="add-condition-buttons">
                    <button type="button" class="btn btn-sm btn-success" 
                            data-action="add-condition"
                            data-group-id="${groupId}"
                            data-operator="AND">
                        <i class="fas fa-plus"></i> ${t.buttons?.add_and_condition || '&& Умова'}
                    </button>
                    <button type="button" class="btn btn-sm btn-info" 
                            data-action="add-condition"
                            data-group-id="${groupId}"
                            data-operator="OR">
                        <i class="fas fa-plus"></i> ${t.buttons?.add_or_condition || '|| Умова'}
                    </button>
                </div>
            </div>
        `;
        
        this.groupsContainer.insertAdjacentHTML('beforeend', html);
        this.updateHiddenInput();
    }
    
    addCondition(groupId, operator) {
        const container = this.groupsContainer.querySelector(`[data-group-id="${groupId}"].conditions-container`);
        if (!container) return;
        
        container.insertAdjacentHTML('beforeend', this.createCondition(groupId, operator));
        this.updateHiddenInput();
    }
    
    removeCondition(conditionId, groupId) {
        const condition = this.groupsContainer.querySelector(`[data-condition-id="${conditionId}"]`);
        const container = this.groupsContainer.querySelector(`[data-group-id="${groupId}"].conditions-container`);
        
        if (!condition || !container) return;
        
        // Не дозволяємо видалити останню умову в групі
        if (container.querySelectorAll('.condition-row').length <= 1) {
            const t = window.logicConditionTranslations || {};
            alert(t.alerts?.min_one_condition || 'Група повинна містити хоча б одну умову!');
            return;
        }
        
        condition.remove();
        this.updateHiddenInput();
    }
    
    removeGroup(groupId) {
        const group = this.groupsContainer.querySelector(`[data-group-id="${groupId}"].logic-group`);
        
        if (!group) return;
        
        // Не дозволяємо видалити останню групу
        if (this.groupsContainer.querySelectorAll('.logic-group').length <= 1) {
            const t = window.logicConditionTranslations || {};
            alert(t.alerts?.min_one_group || 'Повинна залишитися хоча б одна група!');
            return;
        }
        
        group.remove();
        this.updateHiddenInput();
    }
    
    updateHiddenInput() {
        const structure = {
            groups: []
        };
        
        this.groupsContainer.querySelectorAll('.logic-group').forEach((group, groupIndex) => {
            const groupData = {
                operator: groupIndex === 0 ? null : (group.classList.contains('and-group') ? 'AND' : 'OR'),
                conditions: []
            };
            
            group.querySelectorAll('.condition-row').forEach((condition, condIndex) => {
                const conditionData = {
                    operator: condIndex === 0 ? null : (condition.querySelector('.condition-operator-badge.and') ? 'AND' : 'OR'),
                    left_operand: condition.querySelector('.left-operand').value,
                    comparison_operator: condition.querySelector('.operator-select select').value,
                    right_operand: condition.querySelector('.right-operand').value
                };
                groupData.conditions.push(conditionData);
            });
            
            structure.groups.push(groupData);
        });
        
        this.hiddenInput.value = JSON.stringify(structure);
        
        // Оновлюємо preview
        this.updatePreview(structure);
    }
    
    // Оновити preview умови
    updatePreview(structure) {
        const previewCode = this.form.closest('.logic-condition-dynamic-form')?.querySelector('.preview-code code');
        if (!previewCode) return;
        
        const code = this.generatePreviewCode(structure);
        previewCode.textContent = code;
    }
    
    // Генерувати код для preview
    generatePreviewCode(structure) {
        const t = window.logicConditionTranslations || {};
        
        if (!structure || !structure.groups || structure.groups.length === 0) {
            return t.preview?.empty_state || '// Додайте умови для перегляду';
        }
        
        const lines = [];
        lines.push('if (');
        
        structure.groups.forEach((group, groupIndex) => {
            if (groupIndex > 0) {
                lines.push(`  ${group.operator || 'AND'} (`);
            } else {
                lines.push('  (');
            }
            
            group.conditions.forEach((condition, condIndex) => {
                const indent = '    ';
                const left = condition.left_operand || '???';
                const op = this.getOperatorSymbol(condition.comparison_operator);
                const right = condition.right_operand || '???';
                
                let conditionLine = '';
                if (condIndex > 0) {
                    conditionLine = `${indent}${condition.operator || 'AND'} `;
                } else {
                    conditionLine = indent;
                }
                
                // Формуємо умову в залежності від оператора
                if (condition.comparison_operator === 'is_empty') {
                    conditionLine += `isEmpty(${left})`;
                } else if (condition.comparison_operator === 'is_not_empty') {
                    conditionLine += `!isEmpty(${left})`;
                } else if (condition.comparison_operator === 'contains') {
                    conditionLine += `${left}.includes(${right})`;
                } else if (condition.comparison_operator === 'not_contains') {
                    conditionLine += `!${left}.includes(${right})`;
                } else if (condition.comparison_operator === 'starts_with') {
                    conditionLine += `${left}.startsWith(${right})`;
                } else if (condition.comparison_operator === 'not_starts_with') {
                    conditionLine += `!${left}.startsWith(${right})`;
                } else if (condition.comparison_operator === 'ends_with') {
                    conditionLine += `${left}.endsWith(${right})`;
                } else if (condition.comparison_operator === 'not_ends_with') {
                    conditionLine += `!${left}.endsWith(${right})`;
                } else if (condition.comparison_operator === 'regex_match') {
                    conditionLine += `/${right}/.test(${left})`;
                } else if (condition.comparison_operator === 'regex_not_match') {
                    conditionLine += `!/${right}/.test(${left})`;
                } else {
                    conditionLine += `${left} ${op} ${right}`;
                }
                
                lines.push(conditionLine);
            });
            
            lines.push('  )');
        });
        
        lines.push(') {');
        lines.push(`  ${t.preview?.code_comment || '// Нові умови тут...'}`);
        lines.push('}');
        
        return lines.join('\n');
    }
    
    // Отримати символ оператора
    getOperatorSymbol(operator) {
        const symbols = {
            '==': '==',
            '!=': '!=',
            '>': '>',
            '<': '<',
            '>=': '>=',
            '<=': '<='
        };
        return symbols[operator] || operator;
    }
    
    // Завантажити існуючу структуру
    loadStructure(structure) {
        if (!structure || !structure.groups) return;
        
        // Очищаємо контейнер
        this.groupsContainer.innerHTML = '';
        this.groupCounter = 0;
        this.conditionCounter = 0;
        
        // Додаємо групи
        structure.groups.forEach((group, groupIndex) => {
            const groupOperator = groupIndex === 0 ? null : group.operator;
            this.addGroup(groupOperator);
            
            // Отримуємо щойно створену групу (остання в списку)
            const groups = this.groupsContainer.querySelectorAll('.logic-group');
            const createdGroup = groups[groups.length - 1];
            const groupId = createdGroup.dataset.groupId;
            
            // Знаходимо контейнер умов в цій групі
            const container = createdGroup.querySelector('.conditions-container');
            
            if (!container) {
                console.error('Conditions container not found for group:', groupId);
                return;
            }
            
            // Очищаємо дефолтну умову
            container.innerHTML = '';
            
            // Додаємо умови
            group.conditions.forEach((condition, condIndex) => {
                const condOperator = condIndex === 0 ? null : condition.operator;
                container.insertAdjacentHTML('beforeend', this.createCondition(
                    groupId,
                    condOperator,
                    condition.left_operand || '',
                    condition.comparison_operator || '==',
                    condition.right_operand || ''
                ));
            });
        });
        
        this.updateHiddenInput();
    }
}

// WeakMap для зберігання екземплярів форм (не витікає пам'ять)
const formInstances = new WeakMap();

// Внутрішня функція ініціалізації форми
function initializeLogicForm(form) {
    // Перевіряємо чи форма вже ініціалізована через data-атрибут
    if (form.dataset.logicInitialized === 'true') {
        return formInstances.get(form);
    }
    
    // Позначаємо форму як ініціалізовану ПЕРЕД створенням екземпляра
    form.dataset.logicInitialized = 'true';
    
    const instance = new LogicConditionForm(form);
    formInstances.set(form, instance);
    
    // Завантажуємо збережену структуру з script тега якщо є
    const scriptTag = form.querySelector('script.logic-initial-data');
    if (scriptTag) {
        console.log('Found script tag for form:', form.dataset.formId);
        console.log('Script content:', scriptTag.textContent);
        try {
            const structure = JSON.parse(scriptTag.textContent);
            console.log('Parsed structure:', structure);
            if (structure && structure.groups && structure.groups.length > 0) {
                instance.loadStructure(structure);
            }
        } catch (e) {
            console.error('Failed to parse initial structure:', e);
            console.error('Script content was:', scriptTag.textContent);
        }
    }
    
    return instance;
}

// Глобальна функція для ініціалізації форм в модалці
// Викликайте цю функцію після створення/клонування модалки
// Приймає ID модалки або сам елемент модалки
window.initLogicConditionForms = function(modalIdOrElement) {
    let modalElement;
    
    // Визначаємо чи це ID чи елемент
    if (typeof modalIdOrElement === 'string') {
        modalElement = document.getElementById(modalIdOrElement);
        if (!modalElement) {
            console.warn(`Modal with id "${modalIdOrElement}" not found`);
            return;
        }
    } else {
        modalElement = modalIdOrElement;
    }
    
    // Шукаємо тільки форми логіки в цій модалці
    const forms = modalElement.querySelectorAll('.logic-condition-dynamic-form');
    
    if (forms.length === 0) {
        return;
    }
    
    forms.forEach(form => {
        // Якщо форма вже ініціалізована - пропускаємо
        if (form.dataset.logicInitialized === 'true') {
            return;
        }
        
        // Якщо форма має groups-container з контентом - це клонована форма
        const groupsContainer = form.querySelector('.groups-container');
        if (groupsContainer && groupsContainer.children.length > 0) {
            groupsContainer.innerHTML = '';
        }
        
        initializeLogicForm(form);
    });
};

// Ініціалізація існуючих форм при завантаженні
document.addEventListener('DOMContentLoaded', function() {
    // Знаходимо всі форми на сторінці
    const allForms = document.querySelectorAll('.logic-condition-dynamic-form');
    
    // Фільтруємо форми - ігноруємо ті що в template контейнері
    const formsToInit = Array.from(allForms).filter(form => {
        const isInTemplateContainer = form.closest('#events-default-popovers-wrap');
        return !isInTemplateContainer;
    });
    
    formsToInit.forEach(form => {
        initializeLogicForm(form);
    });
});
